import { useState } from 'react';

interface ComponentVariantSelectorProps {
  buttonVariant: string;
  cardVariant: string;
  tableVariant: string;
  onButtonVariantChange: (variant: string) => void;
  onCardVariantChange: (variant: string) => void;
  onTableVariantChange: (variant: string) => void;
  primaryColor?: string;
  disabled?: boolean;
}

const buttonVariants = [
  { value: 'solid', label: 'Solid', description: 'Botão sólido com cor de fundo' },
  { value: 'outline', label: 'Outline', description: 'Botão com borda e fundo transparente' },
  { value: 'ghost', label: 'Ghost', description: 'Botão sem borda, apenas texto' },
  { value: 'gradient', label: 'Gradient', description: 'Botão com gradiente de cores' },
  { value: 'glass', label: 'Glass', description: 'Efeito glassmorphism' },
];

const cardVariants = [
  { value: 'flat', label: 'Flat', description: 'Card sem elevação' },
  { value: 'elevated', label: 'Elevated', description: 'Card com sombra' },
  { value: 'bordered', label: 'Bordered', description: 'Card com borda' },
  { value: 'glass', label: 'Glass', description: 'Efeito glassmorphism' },
];

const tableVariants = [
  { value: 'striped', label: 'Striped', description: 'Linhas alternadas' },
  { value: 'bordered', label: 'Bordered', description: 'Com bordas' },
  { value: 'minimal', label: 'Minimal', description: 'Estilo minimalista' },
  { value: 'modern', label: 'Modern', description: 'Estilo moderno com espaçamento' },
];

export function ComponentVariantSelector({
  buttonVariant,
  cardVariant,
  tableVariant,
  onButtonVariantChange,
  onCardVariantChange,
  onTableVariantChange,
  primaryColor = '59 130 246',
  disabled,
}: ComponentVariantSelectorProps) {
  const [activeTab, setActiveTab] = useState<'button' | 'card' | 'table'>('button');

  const renderButtonPreview = (variant: string) => {
    const baseClasses = 'px-4 py-2 rounded-lg font-medium transition-all';
    
    switch (variant) {
      case 'solid':
        return (
          <button
            className={baseClasses}
            style={{ backgroundColor: `rgb(${primaryColor})`, color: 'white' }}
          >
            Botão Solid
          </button>
        );
      case 'outline':
        return (
          <button
            className={`${baseClasses} bg-transparent border-2`}
            style={{ borderColor: `rgb(${primaryColor})`, color: `rgb(${primaryColor})` }}
          >
            Botão Outline
          </button>
        );
      case 'ghost':
        return (
          <button
            className={`${baseClasses} bg-transparent`}
            style={{ color: `rgb(${primaryColor})` }}
          >
            Botão Ghost
          </button>
        );
      case 'gradient':
        return (
          <button
            className={baseClasses}
            style={{
              background: `linear-gradient(135deg, rgb(${primaryColor}), rgb(147 51 234))`,
              color: 'white',
            }}
          >
            Botão Gradient
          </button>
        );
      case 'glass':
        return (
          <button
            className={`${baseClasses} backdrop-blur-md border border-white/20`}
            style={{
              backgroundColor: `rgba(${primaryColor.replace(/ /g, ', ')}, 0.2)`,
              color: 'white',
            }}
          >
            Botão Glass
          </button>
        );
      default:
        return null;
    }
  };

  const renderCardPreview = (variant: string) => {
    const baseClasses = 'p-4 rounded-xl';
    
    switch (variant) {
      case 'flat':
        return (
          <div className={`${baseClasses} bg-surface-800`}>
            <h4 className="font-medium text-white mb-2">Card Flat</h4>
            <p className="text-sm text-gray-400">Sem elevação ou sombra</p>
          </div>
        );
      case 'elevated':
        return (
          <div className={`${baseClasses} bg-surface-800 shadow-lg shadow-black/20`}>
            <h4 className="font-medium text-white mb-2">Card Elevated</h4>
            <p className="text-sm text-gray-400">Com sombra para elevação</p>
          </div>
        );
      case 'bordered':
        return (
          <div className={`${baseClasses} bg-surface-800 border border-surface-600`}>
            <h4 className="font-medium text-white mb-2">Card Bordered</h4>
            <p className="text-sm text-gray-400">Com borda visível</p>
          </div>
        );
      case 'glass':
        return (
          <div
            className={`${baseClasses} backdrop-blur-md border border-white/10`}
            style={{ backgroundColor: 'rgba(30, 41, 59, 0.5)' }}
          >
            <h4 className="font-medium text-white mb-2">Card Glass</h4>
            <p className="text-sm text-gray-400">Efeito glassmorphism</p>
          </div>
        );
      default:
        return null;
    }
  };

  const renderTablePreview = (variant: string) => {
    const rows = [
      { name: 'Item 1', value: 'R$ 100,00' },
      { name: 'Item 2', value: 'R$ 200,00' },
      { name: 'Item 3', value: 'R$ 300,00' },
    ];

    switch (variant) {
      case 'striped':
        return (
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-700">
                <th className="text-left py-2 px-3 text-gray-400 text-sm">Nome</th>
                <th className="text-right py-2 px-3 text-gray-400 text-sm">Valor</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-surface-800' : 'bg-surface-900'}>
                  <td className="py-2 px-3 text-white text-sm">{row.name}</td>
                  <td className="py-2 px-3 text-white text-sm text-right">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case 'bordered':
        return (
          <table className="w-full border border-surface-700">
            <thead>
              <tr className="bg-surface-800 border-b border-surface-700">
                <th className="text-left py-2 px-3 text-gray-400 text-sm border-r border-surface-700">Nome</th>
                <th className="text-right py-2 px-3 text-gray-400 text-sm">Valor</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b border-surface-700 last:border-b-0">
                  <td className="py-2 px-3 text-white text-sm border-r border-surface-700">{row.name}</td>
                  <td className="py-2 px-3 text-white text-sm text-right">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case 'minimal':
        return (
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left py-2 px-3 text-gray-400 text-sm">Nome</th>
                <th className="text-right py-2 px-3 text-gray-400 text-sm">Valor</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b border-surface-800">
                  <td className="py-2 px-3 text-white text-sm">{row.name}</td>
                  <td className="py-2 px-3 text-white text-sm text-right">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      case 'modern':
        return (
          <div className="space-y-2">
            {rows.map((row, i) => (
              <div key={i} className="flex justify-between items-center bg-surface-800 rounded-lg px-4 py-3">
                <span className="text-white text-sm">{row.name}</span>
                <span className="text-white text-sm font-medium">{row.value}</span>
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-white">Variantes de Componentes</h3>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-surface-700 pb-2">
        {(['button', 'card', 'table'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
              activeTab === tab
                ? 'bg-surface-800 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {tab === 'button' ? 'Botões' : tab === 'card' ? 'Cards' : 'Tabelas'}
          </button>
        ))}
      </div>

      {/* Button Variants */}
      {activeTab === 'button' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {buttonVariants.map((variant) => (
            <button
              key={variant.value}
              onClick={() => onButtonVariantChange(variant.value)}
              disabled={disabled}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                buttonVariant === variant.value
                  ? 'border-primary-500 bg-primary-500/10'
                  : 'border-surface-700 bg-surface-800 hover:border-surface-600'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="mb-3">{renderButtonPreview(variant.value)}</div>
              <h4 className="font-medium text-white mb-1">{variant.label}</h4>
              <p className="text-sm text-gray-400">{variant.description}</p>
            </button>
          ))}
        </div>
      )}

      {/* Card Variants */}
      {activeTab === 'card' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cardVariants.map((variant) => (
            <button
              key={variant.value}
              onClick={() => onCardVariantChange(variant.value)}
              disabled={disabled}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                cardVariant === variant.value
                  ? 'border-primary-500 bg-primary-500/10'
                  : 'border-surface-700 bg-surface-900 hover:border-surface-600'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="mb-3">{renderCardPreview(variant.value)}</div>
            </button>
          ))}
        </div>
      )}

      {/* Table Variants */}
      {activeTab === 'table' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tableVariants.map((variant) => (
            <button
              key={variant.value}
              onClick={() => onTableVariantChange(variant.value)}
              disabled={disabled}
              className={`p-4 rounded-xl border-2 transition-all text-left ${
                tableVariant === variant.value
                  ? 'border-primary-500 bg-primary-500/10'
                  : 'border-surface-700 bg-surface-900 hover:border-surface-600'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <h4 className="font-medium text-white mb-3">{variant.label}</h4>
              <div className="bg-surface-900 rounded-lg p-2 overflow-hidden">
                {renderTablePreview(variant.value)}
              </div>
              <p className="text-sm text-gray-400 mt-2">{variant.description}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ComponentVariantSelector;
